import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import militaryData from '../json/militaryData.json';
import axios from 'axios';
import updateTokenUsage from './updateTokenUsage';
import { ThreeDots } from 'react-loader-spinner';
// Import the O4 skills screen so we can embed its functionality directly
import O4SkillsScreen from './o4SkillsScreen.js';
import { AI_FUNCTIONS_BASE_URL as FUNCTIONS_BASE_URL } from '../config/api';
import { enhanceEducationProject } from './educationProjectEnhancement';
import {
  FIELD_LIMITS,
  PATTERNS,
  hasResumeEvidence,
  sanitizeGpaText,
  sanitizeIntegerText,
  sanitizeLine,
  sanitizeMultiline,
  sanitizeResumeFormData,
  sanitizeYearText,
} from './formValidation';
import HiringAuthoritySelect from './HiringAuthoritySelect';

const SectionInspector = () => null;

const cleanLabelPart = (value) => String(value || '').trim();

const formatWorkEntryLabel = (jobTitle, employer, index) => {
  const title = cleanLabelPart(jobTitle);
  const organization = cleanLabelPart(employer);

  if (title && organization) {
    return `${title} - ${organization}`;
  }

  return title || organization || `Work Experience ${index + 1}`;
};

const formatEducationEntryLabel = (institution, degree, index) => {
  const school = cleanLabelPart(institution);
  const credential = cleanLabelPart(degree);

  if (school && credential) {
    return `${school} - ${credential}`;
  }

  return school || credential || `Education ${index + 1}`;
};


// Helper to capitalize phase ids into human‑readable labels.
const capitalizePhase = (phase) => {
  return phase
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase());
};

const withVisibilityDefaults = (data = {}) => ({
  ...data,
  workHistory: Array.isArray(data.workHistory)
    ? data.workHistory.map((job) => ({
        ...job,
        showOnResume: job?.showOnResume !== false,
      }))
    : [],
  educationHistory: Array.isArray(data.educationHistory)
    ? data.educationHistory.map((edu) => ({
        ...edu,
        showOnResume: edu?.showOnResume !== false,
        showMinor: edu?.showMinor !== false,
        showGPA: edu?.showGPA !== false,
      }))
    : [],
});

/**
 * A multi‑phase resume builder component based on react‑hook‑form. This
 * component collects all résumé information (personal info, summary,
 * military details, education, work experience, volunteer work, skills,
 * certifications, training, references, languages, and organizations) and
 * keeps the global formData in sync via watch(). Each phase can be
 * selected via navigation buttons. Dynamic list sections (like
 * educationHistory or workHistory) use useFieldArray to add and remove
 * entries. Date fields use native HTML date inputs to ensure mobile
 * usability without requiring external calendar components.
 */
// Accept setCurrentScreen to enable navigation to other screens (e.g., O4 skills search)
const Form = ({
  formData = {},
  setFormData,
  setTokenUsage,
  setUsagePercentage,
  tokenThreshold,
  setCurrentScreen,
}) => {
  // Initialize react-hook-form with default values. We merge any passed in
  // form data and default the showProfessionalSummary checkbox to true
  // if it is undefined. This ensures the checkbox remains checked on a
  // newly created résumé.
  const { register, control, watch, setValue, reset } = useForm({
    defaultValues: {
      ...withVisibilityDefaults(formData),
      showProfessionalSummary:
        formData?.showProfessionalSummary ?? true,
    },
  });

  // When new data is loaded from the server (indicated by the
  // __loadedFromServer flag), reset the form with the clean data. This
  // effect updates whenever the formData prop changes. We also reapply
  // the showProfessionalSummary default if it wasn't provided.
  useEffect(() => {
    if (formData && formData.__loadedFromServer) {
      const { __loadedFromServer, ...cleanData } = formData;
      reset({
        ...withVisibilityDefaults(cleanData),
        showProfessionalSummary:
          cleanData.showProfessionalSummary ?? true,
      });
    }
  }, [formData, reset]);

  // Track which phase is currently active. Defaults to personal info.
  const [currentPhase, setCurrentPhase] = useState('personalInfo');

  /**
   * Wrapper around setFormData used by the embedded O4 skills component.
   * When the O4 screen updates the skills and selectedSkills arrays it
   * mutates the top‑level formData. Because the skills inputs in this
   * form are controlled via react‑hook‑form, we need to mirror those
   * changes into the form state using setValue. This helper applies
   * both updates: it forwards the updated resume to the parent via
   * setFormData and synchronises the ``skills`` array in the form.
   *
   * @param {object} updatedData The new formData object from O4 skills.
   */
  const handleO4FormDataUpdate = (updatedData) => {
    // Update the parent state with the entire object
    if (typeof setFormData === 'function') {
      setFormData(updatedData);
    }
    // If a new skills array exists, update react‑hook‑form's value
    if (Array.isArray(updatedData?.skills)) {
      setValue('skills', updatedData.skills);
    }
    // Note: we intentionally avoid calling setValue for selectedSkills
    // because it is not registered as a form field. Updating formData
    // (via setFormData) suffices for propagating these changes to
    // downstream components.
  };

  // Whenever the form data changes, update the global state. The watch
  // subscription triggers on every form field change. On unmount
  // (component destruction), we unsubscribe to avoid memory leaks.
  useEffect(() => {
    const subscription = watch((value) => {
      if (typeof setFormData === "function") {
        // Deep clone so we don’t mutate react-hook-form’s state
        const cleaned = sanitizeResumeFormData(JSON.parse(JSON.stringify(value)));

        // Work history
        if (Array.isArray(cleaned.workHistory)) {
          cleaned.workHistory.forEach((job) => {
            job.startDate = normalizeDate(job.startDate);
            job.endDate = normalizeDate(job.endDate);
          });
        }

        // Volunteer work
        if (Array.isArray(cleaned.volunteerWork)) {
          cleaned.volunteerWork.forEach((vol) => {
            vol.startDate = normalizeDate(vol.startDate);
            vol.endDate = normalizeDate(vol.endDate);
          });
        }

        // Certifications
        if (Array.isArray(cleaned.certifications)) {
          cleaned.certifications.forEach((cert) => {
            cert.issueDate = normalizeDate(cert.issueDate);
            cert.expiryDate = normalizeDate(cert.expiryDate);
          });
        }

        // Education history
        if (Array.isArray(cleaned.educationHistory)) {
          cleaned.educationHistory.forEach((edu) => {
            edu.startDate = normalizeDate(edu.startDate);
            edu.endDate = normalizeDate(edu.endDate);
            edu.showOnResume = edu?.showOnResume !== false;
          });
        }

        setFormData(cleaned);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, setFormData]);


  // Configure dynamic sections with useFieldArray. Each call returns
  // fields, append, and remove helpers. The name corresponds to the
  // property in formData. When using nested objects or arrays,
  // react‑hook‑form automatically maps them via dot notation.
  const educationArray = useFieldArray({ control, name: 'educationHistory' });
  const workArray = useFieldArray({ control, name: 'workHistory' });
  const volunteerArray = useFieldArray({ control, name: 'volunteerWork' });
  const skillsArray = useFieldArray({ control, name: 'skills' });
  const certificationsArray = useFieldArray({ control, name: 'certifications' });
  const trainingArray = useFieldArray({ control, name: 'training' });
  const referencesArray = useFieldArray({ control, name: 'references' });
  const languagesArray = useFieldArray({ control, name: 'languages' });
  const organizationsArray = useFieldArray({ control, name: 'organizations' });
  // New list sections: interests/hobbies and awards/honors
  const interestsArray = useFieldArray({ control, name: 'interests' });
  const awardsArray = useFieldArray({ control, name: 'awards' });
  const [generatedSummary, setGeneratedSummary] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [projectEnhancement, setProjectEnhancement] = useState({
    educationIndex: null,
    projectIndex: null,
    status: 'idle',
    text: '',
    error: '',
  });
  const [collapsedEducationIds, setCollapsedEducationIds] = useState(
    () => new Set(),
  );
  const [collapsedWorkIds, setCollapsedWorkIds] = useState(
    () => new Set(),
  );

  const toggleCollapsedEntry = (setCollapsedIds, entryId) => {
    setCollapsedIds((current) => {
      const next = new Set(current);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  };

  const collapseAllEntries = (setCollapsedIds, fields) => {
    setCollapsedIds(new Set(fields.map((field) => field.id)));
  };

  const expandAllEntries = (setCollapsedIds) => {
    setCollapsedIds(new Set());
  };

  // --- UI state for collapsible skills sections ---
  // The skills phase combines two distinct parts: (1) the intelligent
  // search and recommendation widget (O4SkillsScreen) and (2) the list
  // of currently selected skills.  To reduce visual clutter and give
  // users more control over what is displayed, we wrap each of these
  // parts in an HTML <details> element.  The browser provides
  // built‑in show/hide behaviour for these sections without requiring
  // any additional state or hooks.  By default both sections are
  // expanded; users can click the summary to collapse or expand them.
  


  // Define the phases order including the new professional summary phase.
  const phases = [
    'personalInfo',
    'militaryInfo',
    'educationHistory',
    'workHistory',
    'volunteerWork',
    'certifications',
    'training',
    'references',
    'languages',
    // Insert interests/hobbies and awards/honors after languages
    'interests',
    'awards',
    'organizations',
    'skills',
    'professionalSummary'
  ];

  

  // Helper to fetch rank options based on selected branch.
  const getRanksForBranch = (branch) => {
    const branchData = militaryData.branches.find(
      (b) => b.name.toLowerCase() === String(branch).toLowerCase(),
    );
    return branchData ? branchData.ranks : [];
  };

  // Render personal information fields. This phase collects basic
  // contact and address information. Authority is omitted in this
  // implementation for simplicity; it can be added later if needed.
  const renderPersonalInfo = () => {
    return (
      <div className="phase-section">
        <h2>Personal Information</h2>
        <div>
          <label>First Name</label>
          <input className="themed-input"{...lineField('personalInfo.firstName', FIELD_LIMITS.shortText)} />
        </div>
        <div>
          <label>Last Name</label>
          <input className="themed-input"{...lineField('personalInfo.lastName', FIELD_LIMITS.shortText)} />
        </div>
        <div>
          <label>Phone Number</label>
          <input className="themed-input" type="tel" {...lineField('personalInfo.phoneNumber', FIELD_LIMITS.phone, { pattern: PATTERNS.phone })} />
        </div>
        <div>
          <label>Email</label>
          <input className="themed-input" type="email" {...lineField('personalInfo.email', FIELD_LIMITS.email, { pattern: PATTERNS.email })} />
        </div>
        <div>
          <label>Social Presence</label>
          <input className="themed-input" {...lineField('personalInfo.socialPresence', FIELD_LIMITS.url)} />
        </div>
        <div>
          <label>Street</label>
          <input className="themed-input"{...lineField('personalInfo.street', FIELD_LIMITS.mediumText)} />
        </div>
        <div>
          <label>City</label>
          <input className="themed-input"{...lineField('personalInfo.city', FIELD_LIMITS.shortText)} />
        </div>
        <div>
          <label>State</label>
          <input className="themed-input"{...lineField('personalInfo.state', FIELD_LIMITS.shortText)} />
        </div>
        <div>
          <label>Postal Code</label>
          <input className="themed-input"{...lineField('personalInfo.postalCode', FIELD_LIMITS.postalCode)} />
        </div>
        <div>
          <label>Country</label>
          <input className="themed-input"{...lineField('personalInfo.country', FIELD_LIMITS.shortText)} />
        </div>
        <div>
          <label>Federal Hiring Authorities</label>
          <HiringAuthoritySelect
            value={watch('personalInfo.authority') || []}
            onChange={(nextAuthorities) => setValue('personalInfo.authority', nextAuthorities, {
              shouldDirty: true,
              shouldTouch: true,
            })}
          />
        </div>

      </div>
    );
  };


  // Format yyyy-mm-dd → mm/dd/yyyy
const normalizeDate = (val) => {
  const value = String(val || '').trim();
  if (!value) return '';
  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) return value;
  const usMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!usMatch) return value;
  const [, month, day, year] = usMatch;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

const makeLineField = (registerFn) => (name, maxLength = FIELD_LIMITS.mediumText, options = {}) => ({
  ...registerFn(name, {
    setValueAs: (value) => sanitizeLine(value, maxLength),
    ...options,
  }),
  maxLength,
  'aria-label': name.replace(/\.(\d+)\./g, ' $1 ').replace(/\./g, ' '),
});

const makeMultilineField = (registerFn) => (name, maxLength = FIELD_LIMITS.longText, options = {}) => ({
  ...registerFn(name, {
    setValueAs: (value) => sanitizeMultiline(value, maxLength),
    ...options,
  }),
  maxLength,
  'aria-label': name.replace(/\.(\d+)\./g, ' $1 ').replace(/\./g, ' '),
});

const makeIntegerField = (registerFn) => (name, max = 168) => ({
  ...registerFn(name, {
    setValueAs: (value) => sanitizeIntegerText(value, 0, max),
    pattern: PATTERNS.hours,
  }),
  inputMode: 'numeric',
  pattern: '[0-9]*',
  maxLength: FIELD_LIMITS.hours,
  'aria-label': name.replace(/\.(\d+)\./g, ' $1 ').replace(/\./g, ' '),
});

const makeGpaField = (registerFn) => (name) => ({
  ...registerFn(name, {
    setValueAs: sanitizeGpaText,
    pattern: PATTERNS.gpa,
  }),
  inputMode: 'decimal',
  maxLength: FIELD_LIMITS.gpa,
  'aria-label': name.replace(/\.(\d+)\./g, ' $1 ').replace(/\./g, ' '),
});

const makeYearField = (registerFn) => (name) => ({
  ...registerFn(name, {
    setValueAs: sanitizeYearText,
    pattern: PATTERNS.year,
  }),
  inputMode: 'numeric',
  pattern: '[0-9]{4}',
  maxLength: FIELD_LIMITS.year,
  'aria-label': name.replace(/\.(\d+)\./g, ' $1 ').replace(/\./g, ' '),
});

const lineField = makeLineField(register);
const multilineField = makeMultilineField(register);
const integerField = makeIntegerField(register);
const gpaField = makeGpaField(register);
const yearField = makeYearField(register);


const handleGenerateSummary = async () => {
  if (!hasResumeEvidence(formData)) {
    setGeneratedSummary('');
    setError('Add profile details before generating a summary.');
    return;
  }

  setIsLoading(true);
  setError(null);

  try {
    const summaryData = {
      militaryInfo: formData.militaryInfo || {},
      educationHistory: (formData.educationHistory || []).filter(
        (edu) => edu?.showOnResume !== false,
      ),
      workHistory: (formData.workHistory || []).filter(
        (job) => job?.showOnResume !== false,
      ),
      volunteerWork: formData.volunteerWork || [],
      skills: formData.skills || [],
      certifications: formData.certifications || [],
      languages: formData.languages || [],
      interests: formData.interests || [],
      awards: formData.awards || [],
      organizations: formData.organizations || [],
    };

    const data = {
      prompt: 'summaryGenerator',      // backend uses o4-mini for this prompt
      // Accept both user and userID fields from formData.  Some code
      // stores the identifier under formData.userID rather than
      // formData.user, so fall back accordingly.  If neither is set,
      // undefined will be passed and the server will respond with an
      // informative error.
      userID: formData.user || formData.userID,
      userSummary: summaryData,
    };

    // Uses FUNCTIONS_BASE_URL so you can point to prod/stage without code changes
    const response = await axios.post(
      `${FUNCTIONS_BASE_URL}/api/jobSeriesSearchHttp`,
      data,
      { headers: { 'Content-Type': 'application/json' } },
    );

    // Some backends (e.g. Azure Functions) return the JSON body as a
    // string rather than already parsed.  Additionally, the summary
    // text may be nested under different keys depending on the API
    // shape.  Normalise the response here so we always extract a
    // summary string.  If parsing fails, parsedData will remain an
    // empty object so the generated summary will default to an
    // empty string.
    let aiData = response.data;
    let parsedData;
    try {
      parsedData = typeof aiData === 'string' ? JSON.parse(aiData) : aiData;
    } catch (parseErr) {
      console.error('Failed to parse summary JSON:', parseErr);
      parsedData = {};
    }
    // Extract the summary text.  Prefer the explicit 'summary' field,
    // fall back to top‑level content fields if present (e.g. OpenAI
    // chat completions return choices[0].message.content).
    const summaryText =
      parsedData.summary ??
      parsedData.content ??
      parsedData.choices?.[0]?.message?.content ??
      '';
    setGeneratedSummary(summaryText);
    setError(null);

    try {
      await updateTokenUsage(formData.user, tokenThreshold, setTokenUsage, setUsagePercentage);
    } catch (e) {
      console.error('Failed to update token usage after generating summary:', e);
    }
  } catch (err) {
    if (err?.response?.status === 403) {
      console.error('Token limit exceeded:', err);
      setError('Token limit exceeded. Please contact support for additional tokens.');
    } else {
      console.error('Error generating summary:', err);
      setError('Failed to generate summary. Please try again later.');
    }
  } finally {
    setIsLoading(false);
  }
};


const handleAcceptSummary = () => {
  if (generatedSummary) {
    setValue('professionalSummary', generatedSummary);
    setGeneratedSummary('');
  }
};


  // Render the professional summary. This is a short narrative at the
  // beginning of a private sector résumé that highlights the candidate’s
  // experience and goals.
const renderProfessionalSummary = () => {
  return (
    <div className="phase-section">
      <h2>Professional Summary</h2>
      <textarea
        className="themed-input"
        {...multilineField('professionalSummary', FIELD_LIMITS.summary)}
        rows={4}
        placeholder="Write a brief summary of your professional experience and goals"
      ></textarea>
        <label style={{ marginTop: '10px', display: 'block' }}>
          <input
            type="checkbox"
            {...register('showProfessionalSummary')}
            defaultChecked={formData.showProfessionalSummary ?? true} // force default check
          />
          Show Professional Summary
        </label>

      <div style={{ marginTop: '10px' }}>
        <button
          type="button"
          onClick={handleGenerateSummary}
          className="skills-screen recommendation-button"
          disabled={isLoading}
          aria-busy={isLoading}
        >
          {isLoading ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <ThreeDots
                height="14"
                width="36"
                color="#4c6ef5"
                ariaLabel="loading"
                visible
              />
              Generating summary…
            </span>
          ) : (
            'Generate Summary'
          )}
        </button>
        
      </div>
      {error && (
        <p role="alert" style={{ color: '#b42318', marginTop: '8px' }}>
          {error}
        </p>
      )}

      {generatedSummary && (
        <div className="generated-summary-preview" style={{ marginTop: '10px', padding: '10px', border: '1px solid #ccc' }}>
          <h4>AI-Generated Summary</h4>
          <p>{generatedSummary}</p>
          <button type="button" onClick={handleAcceptSummary}>
            Accept
          </button>
          <button type="button" onClick={() => setGeneratedSummary('')}>
            Dismiss
          </button>
        </div>
      )}
      {/* Inspector for professional summary */}
      <SectionInspector
        sectionName="professionalSummary"
        formData={formData}
        setFormData={setFormData}
        setFieldValue={setValue}
        tokenThreshold={tokenThreshold}
        setTokenUsage={setTokenUsage}
        setUsagePercentage={setUsagePercentage}
      />
    </div>
  );
};

const militaryStatus = watch("militaryInfo.militaryStatus");

useEffect(() => {
  if (militaryStatus === "Select Status" || !militaryStatus) {
    setValue("militaryInfo.militaryBranch", "");
    setValue("militaryInfo.militaryRank", "");
    setValue("militaryInfo.militaryOccupation", "");
    setValue("militaryInfo.militaryLocation", "");
  }
}, [militaryStatus, setValue]);

const renderMilitaryInfo = () => {
  const status = watch('militaryInfo.militaryStatus');
  const branch = watch('militaryInfo.militaryBranch');
  const rankOptions = getRanksForBranch(branch);

  return (
    <div className="phase-section">
      <h2>Military Information</h2>

      {/* Status dropdown (always shown) */}
      <div>
        <label htmlFor="militaryStatus">Status</label>
        <select
          id="militaryStatus"
          className="themed-input"
          {...register('militaryInfo.militaryStatus')}
        >
          <option value="">Select Status</option>
          <option value="activeDuty">Active Duty</option>
          <option value="veteran">Veteran</option>
          <option value="reservist">Reservist</option>
          <option value="nationalGuard">National Guard</option>
        </select>
      </div>

      {/* Only show these fields if a status is selected */}
      {status && (
        <>
          <div>
            <label>Branch</label>
            <select
              className="themed-input"
              {...register('militaryInfo.militaryBranch')}
            >
              <option value="">Select Branch</option>
              {militaryData.branches.map((b) => (
                <option key={b.name} value={b.name}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>Rank</label>
            <select
              className="themed-input"
              {...register('militaryInfo.militaryRank')}
            >
              <option value="">Select Rank</option>
              {rankOptions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>Role/Occupation</label>
            <input
              className="themed-input"
              {...lineField('militaryInfo.militaryOccupation', FIELD_LIMITS.mediumText)}
            />
          </div>
          <div>
            <label>Duration</label>
            <input
              className="themed-input"
              {...lineField('militaryInfo.militaryDuration', FIELD_LIMITS.shortText)}
            />
          </div>

          <div>
            <label>Location/Duty Station</label>
            <input
              className="themed-input"
              {...lineField('militaryInfo.militaryLocation', FIELD_LIMITS.mediumText)}
            />
          </div>
        </>
      )}

      {/* Inspector for military information */}
      <SectionInspector
        sectionName="militaryInfo"
        formData={formData}
        setFormData={setFormData}
        setFieldValue={setValue}
        tokenThreshold={tokenThreshold}
        setTokenUsage={setTokenUsage}
        setUsagePercentage={setUsagePercentage}
      />
      <div
        className="military-work-history-guidance"
        style={{
          marginBottom: '16px',
          padding: '12px',
          border: '1px solid #b8cfe0',
          borderRadius: '8px',
          background: '#eef7ff',
          color: '#07314d',
        }}
      >
        <p style={{ margin: '0 0 8px' }}>
          Use this section for a military service summary. Add military roles,
          duties, accomplishments, hours, supervisors, and mission impact in
          Work History so they appear as federal resume experience.
        </p>
        <button
          type="button"
          onClick={() => setCurrentPhase('workHistory')}
        >
          Add Military Experience in Work History
        </button>
      </div>
    </div>
  );
};




  // Render education history. Each entry collects institution, degree,
  // major, minor, GPA, graduation year, significant projects, and
  // relevant courses. The user can add or remove multiple entries.
const renderEducationHistory = () => {
  return (
    <div className="phase-section">
      <div className="phase-section-heading">
        <h2>Education History</h2>
        <div className="bulk-collapse-controls">
          <button
            type="button"
            onClick={() =>
              collapseAllEntries(
                setCollapsedEducationIds,
                educationArray.fields,
              )
            }
            disabled={educationArray.fields.length === 0}
            aria-label="Collapse all education history"
          >
            Collapse All
          </button>
          <button
            type="button"
            onClick={() => expandAllEntries(setCollapsedEducationIds)}
            disabled={educationArray.fields.length === 0}
            aria-label="Expand all education history"
          >
            Expand All
          </button>
        </div>
      </div>
      {educationArray.fields.map((field, index) => {
        const isCollapsed = collapsedEducationIds.has(field.id);
        const institution =
          watch(`educationHistory.${index}.institution`) || '';
        const degree = watch(`educationHistory.${index}.degree`) || '';
        const showOnResume =
          watch(`educationHistory.${index}.showOnResume`) !== false;
        const summary = [institution, degree].filter(Boolean).join(' | ');
        const entryLabel = formatEducationEntryLabel(
          institution,
          degree,
          index,
        );
        const moveEducation = (targetIndex) => {
          setProjectEnhancement({
            educationIndex: null,
            projectIndex: null,
            status: 'idle',
            text: '',
            error: '',
          });
          educationArray.move(index, targetIndex);
        };

        return (
        <div
          key={field.id}
          className={`entry collapsible-entry ${isCollapsed ? 'is-collapsed' : ''} ${showOnResume ? '' : 'is-hidden-from-resume'}`}
        >
          <div className="entry-heading-row">
            <div className="entry-heading-copy">
              <h3>{entryLabel}</h3>
              <span className="entry-summary">
                {summary || 'Education details not entered'}
              </span>
              {!showOnResume && (
                <span className="entry-visibility-status">
                  Hidden from resume
                </span>
              )}
            </div>
            <div className="entry-header-actions">
              <label className="entry-visibility-toggle">
                <input
                  type="checkbox"
                  {...register(`educationHistory.${index}.showOnResume`)}
                />
                Show on resume
              </label>
              <div
                className="entry-order-controls"
                aria-label={`Reorder education ${index + 1}`}
              >
                <button
                  type="button"
                  onClick={() => moveEducation(index - 1)}
                  disabled={index === 0}
                  aria-label={`Move education ${index + 1} up`}
                >
                  Move up
                </button>
                <button
                  type="button"
                  onClick={() => moveEducation(index + 1)}
                  disabled={index === educationArray.fields.length - 1}
                  aria-label={`Move education ${index + 1} down`}
                >
                  Move down
                </button>
              </div>
              <button
                type="button"
                className="entry-collapse-toggle"
                onClick={() =>
                  toggleCollapsedEntry(setCollapsedEducationIds, field.id)
                }
                aria-expanded={!isCollapsed}
                aria-controls={`education-entry-${field.id}`}
              >
                {isCollapsed ? 'Expand' : 'Collapse'}
              </button>
            </div>
          </div>

          {!isCollapsed && (
          <div id={`education-entry-${field.id}`} className="entry-body">

          {/* Institution */}
          <div>
            <label>Institution</label>
            <input
              className="themed-input"
              {...lineField(`educationHistory.${index}.institution`, FIELD_LIMITS.mediumText)}
            />
          </div>

          {/* Degree */}
          <div>
            <label>Degree</label>
            <input
              className="themed-input"
              {...lineField(`educationHistory.${index}.degree`, FIELD_LIMITS.mediumText)}
            />
          </div>

          {/* Major */}
          <div>
            <label>Major</label>
            <input
              className="themed-input"
              {...lineField(`educationHistory.${index}.major`, FIELD_LIMITS.mediumText)}
            />
          </div>

          {/* Minor */}
          <div>
            <label>Minor</label>
            <input
              className="themed-input"
              {...lineField(`educationHistory.${index}.minor`, FIELD_LIMITS.mediumText)}
            />
            <label style={{ marginLeft: '10px' }}>
              <input
                type="checkbox"
                {...register(`educationHistory.${index}.showMinor`)}
              />{" "}
              Show Minor
            </label>
          </div>

          {/* GPA */}
          <div>
            <label>GPA</label>
            <input
              className="themed-input"
              type="text"
              {...gpaField(`educationHistory.${index}.gpa`)}
            />
            <label style={{ marginLeft: '10px' }}>
              <input
                type="checkbox"
                {...register(`educationHistory.${index}.showGPA`)}
              />{" "}
              Show GPA
            </label>
          </div>

          {/* Graduation Year */}
          <div>
            <label>Graduation Year</label>
            <input
              className="themed-input"
              type="text"
              {...yearField(`educationHistory.${index}.graduationYear`)}
            />
          </div>

          {/* ================= Significant Projects ================= */}
          <div>
            <label>Significant Projects</label>
            {(() => {
              const projects =
                watch(`educationHistory.${index}.significantProjects`) || [];

              const addProject = () => {
                const eduList = watch("educationHistory") || [];
                const updated = [...eduList];
                const entry = updated[index] || {};
                const currentProjects = Array.isArray(entry.significantProjects)
                  ? [...entry.significantProjects]
                  : [];
                if (currentProjects.length >= 20) return;
                currentProjects.push("");
                updated[index] = {
                  ...entry,
                  significantProjects: currentProjects,
                };
                setValue("educationHistory", updated);
              };

              const removeProject = (pIdx) => {
                const eduList = watch("educationHistory") || [];
                const updated = [...eduList];
                const entry = updated[index] || {};
                const currentProjects = Array.isArray(entry.significantProjects)
                  ? [...entry.significantProjects]
                  : [];
                currentProjects.splice(pIdx, 1);
                updated[index] = {
                  ...entry,
                  significantProjects: currentProjects,
                };
                setValue("educationHistory", updated);
              };

              const handleEnhanceProject = async (pIdx) => {
                const eduList = watch("educationHistory") || [];
                const entry = eduList[index] || {};
                const projectText =
                  entry.significantProjects?.[pIdx] || "";

                if (!String(projectText).trim()) {
                  setProjectEnhancement({
                    educationIndex: index,
                    projectIndex: pIdx,
                    status: 'error',
                    text: '',
                    error: 'Add project details before enhancing this entry.',
                  });
                  return;
                }

                setProjectEnhancement({
                  educationIndex: index,
                  projectIndex: pIdx,
                  status: 'loading',
                  text: '',
                  error: '',
                });

                try {
                  const enhancedProject = await enhanceEducationProject({
                    axiosClient: axios,
                    baseUrl: FUNCTIONS_BASE_URL,
                    formData,
                    educationEntry: entry,
                    projectText,
                  });

                  setProjectEnhancement({
                    educationIndex: index,
                    projectIndex: pIdx,
                    status: 'success',
                    text: enhancedProject,
                    error: '',
                  });

                  if (
                    typeof setTokenUsage === 'function' &&
                    typeof setUsagePercentage === 'function'
                  ) {
                    await updateTokenUsage(formData.user, tokenThreshold, setTokenUsage, setUsagePercentage);
                  }
                } catch (err) {
                  console.error("Error enhancing project:", err);
                  const tokenLimitExceeded = err?.response?.status === 403;
                  setProjectEnhancement({
                    educationIndex: index,
                    projectIndex: pIdx,
                    status: 'error',
                    text: '',
                    error: tokenLimitExceeded
                      ? 'Token limit exceeded. Please contact support for additional tokens.'
                      : err?.message ||
                        "Failed to enhance project. Please try again.",
                  });
                }
              };

              const clearProjectEnhancement = () => {
                setProjectEnhancement({
                  educationIndex: null,
                  projectIndex: null,
                  status: 'idle',
                  text: '',
                  error: '',
                });
              };

              const handleAcceptProject = (pIdx) => {
                if (
                  projectEnhancement.status !== 'success' ||
                  projectEnhancement.educationIndex !== index ||
                  projectEnhancement.projectIndex !== pIdx
                ) {
                  return;
                }

                setValue(
                  `educationHistory.${index}.significantProjects.${pIdx}`,
                  projectEnhancement.text,
                  { shouldDirty: true },
                );
                clearProjectEnhancement();
              };

              return (
                <>
                  {Array.isArray(projects) &&
                    projects.map((project, pIdx) => (
                      <div key={pIdx} style={{ marginBottom: "10px" }}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <input
                            className="themed-input"
                            {...multilineField(
                              `educationHistory.${index}.significantProjects.${pIdx}`,
                              FIELD_LIMITS.longText,
                            )}
                            placeholder="e.g., Capstone design project, Data analysis case study"
                          />
                          <button
                            type="button"
                            onClick={() => removeProject(pIdx)}
                            style={{ marginLeft: "4px" }}
                          >
                            Remove
                          </button>
                          <button
                            type="button"
                            onClick={() => handleEnhanceProject(pIdx)}
                            disabled={projectEnhancement.status === 'loading'}
                            style={{ marginLeft: "4px" }}
                          >
                            {projectEnhancement.status === 'loading' &&
                            projectEnhancement.educationIndex === index &&
                            projectEnhancement.projectIndex === pIdx
                              ? "Enhancing…"
                              : "Enhance"}
                          </button>
                        </div>

                        {/* Preview for THIS project only */}
                        {projectEnhancement.status === 'success' &&
                          projectEnhancement.educationIndex === index &&
                          projectEnhancement.projectIndex === pIdx && (
                          <div
                            style={{
                              marginTop: "8px",
                              padding: "8px",
                              border: "1px solid #ccc",
                            }}
                          >
                            <h4>AI-Enhanced Project</h4>
                            <p>{projectEnhancement.text}</p>
                            <button
                              type="button"
                              onClick={() => handleAcceptProject(pIdx)}
                            >
                              Accept
                            </button>
                            <button
                              type="button"
                              onClick={clearProjectEnhancement}
                            >
                              Dismiss
                            </button>
                          </div>
                        )}

                        {projectEnhancement.status === 'error' &&
                          projectEnhancement.educationIndex === index &&
                          projectEnhancement.projectIndex === pIdx && (
                          <p style={{ color: "red" }}>
                            {projectEnhancement.error}
                          </p>
                        )}
                      </div>
                    ))}
                  <button type="button" onClick={addProject}>
                    Add Project
                  </button>
                </>
              );
            })()}
          </div>

          {/* ================= Relevant Courses ================= */}
          <div>
            <label>Relevant Courses</label>
            {(() => {
              const courses =
                watch(`educationHistory.${index}.relevantCourses`) || [];

              const addCourse = () => {
                const eduList = watch("educationHistory") || [];
                const updated = [...eduList];
                const entry = updated[index] || {};
                const currentCourses = Array.isArray(entry.relevantCourses)
                  ? [...entry.relevantCourses]
                  : [];
                if (currentCourses.length >= 40) return;
                currentCourses.push("");
                updated[index] = { ...entry, relevantCourses: currentCourses };
                setValue("educationHistory", updated);
              };

              const removeCourse = (cIdx) => {
                const eduList = watch("educationHistory") || [];
                const updated = [...eduList];
                const entry = updated[index] || {};
                const currentCourses = Array.isArray(entry.relevantCourses)
                  ? [...entry.relevantCourses]
                  : [];
                currentCourses.splice(cIdx, 1);
                updated[index] = { ...entry, relevantCourses: currentCourses };
                setValue("educationHistory", updated);
              };

              return (
                <>
                  {Array.isArray(courses) &&
                    courses.map((course, cIdx) => (
                      <div
                        key={cIdx}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "4px",
                        }}
                      >
                        <input
                          className="themed-input"
                          {...lineField(
                            `educationHistory.${index}.relevantCourses.${cIdx}`,
                            FIELD_LIMITS.listItem,
                          )}
                        />
                        <button
                          type="button"
                          onClick={() => removeCourse(cIdx)}
                          style={{ marginLeft: "4px" }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  <button type="button" onClick={addCourse}>
                    Add Course
                  </button>
                </>
              );
            })()}
          </div>

          <button
            type="button"
            onClick={() => {
              setProjectEnhancement({
                educationIndex: null,
                projectIndex: null,
                status: 'idle',
                text: '',
                error: '',
              });
              educationArray.remove(index);
            }}
          >
            Remove Education
          </button>
          </div>
          )}
        </div>
        );
      })}
      <button
        type="button"
        disabled={educationArray.fields.length >= 25}
        onClick={() =>
          educationArray.append({
            institution: "",
            degree: "",
            major: "",
            minor: "",
            gpa: "",
            graduationYear: "",
            showOnResume: true,
            showMinor: true,
            showGPA: true,
            significantProjects: [],
            relevantCourses: [],
          })
        }
      >
        Add Education
      </button>
      {/* Inspector for education history */}
      <SectionInspector
        sectionName="educationHistory"
        formData={formData}
        setFormData={setFormData}
        setFieldValue={setValue}
        tokenThreshold={tokenThreshold}
        setTokenUsage={setTokenUsage}
        setUsagePercentage={setUsagePercentage}
      />
    </div>
  );
};


  

  // Render work history. Each entry includes details like job title,
  // employer, dates, hours, responsibilities, accomplishments, pay rate,
  // employment type, supervisor, and contact preferences. A "present"
  // checkbox disables the end date when checked.
  const renderWorkHistory = () => {
    return (
      <div className="phase-section">
        <div className="phase-section-heading">
          <h2>Work History</h2>
          <div className="bulk-collapse-controls">
            <button
              type="button"
              onClick={() =>
                collapseAllEntries(setCollapsedWorkIds, workArray.fields)
              }
              disabled={workArray.fields.length === 0}
              aria-label="Collapse all work history"
            >
              Collapse All
            </button>
            <button
              type="button"
              onClick={() => expandAllEntries(setCollapsedWorkIds)}
              disabled={workArray.fields.length === 0}
              aria-label="Expand all work history"
            >
              Expand All
            </button>
          </div>
        </div>
        {workArray.fields.map((field, index) => {
          const present = watch(`workHistory.${index}.present`);
          const showOnResume =
            watch(`workHistory.${index}.showOnResume`) !== false;
          const isCollapsed = collapsedWorkIds.has(field.id);
          const jobTitle = watch(`workHistory.${index}.jobTitle`) || '';
          const employer = watch(`workHistory.${index}.employer`) || '';
          const summary = [jobTitle, employer].filter(Boolean).join(' | ');
          const entryLabel = formatWorkEntryLabel(jobTitle, employer, index);
          return (
            <div
              key={field.id}
              className={`entry collapsible-entry ${isCollapsed ? 'is-collapsed' : ''} ${showOnResume ? '' : 'is-hidden-from-resume'}`}
            >
              <div className="entry-heading-row">
                <div className="entry-heading-copy">
                  <h3>{entryLabel}</h3>
                  <span className="entry-summary">
                    {summary || 'Work details not entered'}
                  </span>
                  {!showOnResume && (
                    <span className="entry-visibility-status">
                      Hidden from resume
                    </span>
                  )}
                </div>
                <div className="entry-header-actions">
                  <label className="entry-visibility-toggle">
                    <input
                      type="checkbox"
                      {...register(`workHistory.${index}.showOnResume`)}
                    />
                    Show on resume
                  </label>
                  <div className="entry-order-controls" aria-label={`Reorder work experience ${index + 1}`}>
                    <button
                      type="button"
                      onClick={() => workArray.move(index, index - 1)}
                      disabled={index === 0}
                      aria-label={`Move work experience ${index + 1} up`}
                    >
                      Move up
                    </button>
                    <button
                      type="button"
                      onClick={() => workArray.move(index, index + 1)}
                      disabled={index === workArray.fields.length - 1}
                      aria-label={`Move work experience ${index + 1} down`}
                    >
                      Move down
                    </button>
                  </div>
                  <button
                    type="button"
                    className="entry-collapse-toggle"
                    onClick={() =>
                      toggleCollapsedEntry(setCollapsedWorkIds, field.id)
                    }
                    aria-expanded={!isCollapsed}
                    aria-controls={`work-entry-${field.id}`}
                  >
                    {isCollapsed ? 'Expand' : 'Collapse'}
                  </button>
                </div>
              </div>
              {!isCollapsed && (
              <div id={`work-entry-${field.id}`} className="entry-body">
              <div>
                <label>Job Title</label>
                <input className="themed-input"{...lineField(`workHistory.${index}.jobTitle`, FIELD_LIMITS.mediumText)} />
              </div>
              <div>
                <label>Employer</label>
                <input className="themed-input"{...lineField(`workHistory.${index}.employer`, FIELD_LIMITS.mediumText)} />
              </div>
              <div>
                <label>Employer Address - City, State</label>
                <input className="themed-input"{...lineField(`workHistory.${index}.employerAddress`, FIELD_LIMITS.mediumText)} />
              </div>
              <div>
                <label>Start Date</label>
                <input className="themed-input"type="date" {...register(`workHistory.${index}.startDate`)} />
              </div>
              <div>
                <label>End Date</label>

                <input
                  className="themed-input"
                  type="date"
                  {...register(`workHistory.${index}.endDate`)}
                  disabled={!!present}
                />

                <label style={{ marginLeft: '10px' }}>
                  <input
                    type="checkbox"
                    {...register(`workHistory.${index}.present`, { value: true })}
                  /> Present
                </label>

              </div>
              <div>
                <label>Hours Per Week</label>
                <input className="themed-input"type="text" {...integerField(`workHistory.${index}.hoursPerWeek`)} />
              </div>
              <div>
                <label>Responsibilities</label>
                <textarea className="themed-input"
                  {...multilineField(`workHistory.${index}.responsibilities`, FIELD_LIMITS.longText)}
                  rows={3}
                ></textarea>
              </div>
              <div>
                <label>Key Accomplishments</label>
                <textarea className="themed-input"
                  {...multilineField(`workHistory.${index}.keyAccomplishments`, FIELD_LIMITS.longText)}
                  rows={3}
                ></textarea>
              </div>
              <div>
                <label>Pay amount (optional)</label>
                <input
                  className="themed-input"
                  inputMode="decimal"
                  placeholder="Example: $28.50"
                  {...lineField(`workHistory.${index}.payAmount`, FIELD_LIMITS.shortText)}
                />
              </div>
              <div>
                <label>Pay basis</label>
                <select className="themed-input" {...register(`workHistory.${index}.payRate`)}>
                  <option value="">Select Pay Basis</option>
                  <option value="biweekly">Biweekly</option>
                  <option value="weekly">Weekly</option>
                  <option value="fee_basis">Fee Basis</option>
                  <option value="per_year">Per Year</option>
                  <option value="per_day">Per Day</option>
                  <option value="per_hour">Per Hour</option>
                  <option value="per_month">Per Month</option>
                  <option value="piece_work">Piece Work</option>
                  <option value="student_stipend">Student Stipend</option>
                  <option value="school_year">School Year</option>
                  <option value="without_compensation">Without Compensation</option>
                </select>
              </div>
              <div>
                <label>Employment Type</label>
                <select className="themed-input" {...register(`workHistory.${index}.employmentType`)}>
                  <option value="">Select Employment Type</option>
                  <option value="fulltime">Full-time</option>
                  <option value="parttime">Part-time</option>
                </select>
              </div>
              <div>
                <label>Supervisor</label>
                <input className="themed-input"{...lineField(`workHistory.${index}.supervisor`, FIELD_LIMITS.mediumText)} />
              </div>
              <div>
                <label>Supervisor Contact</label>
                <input className="themed-input"{...lineField(`workHistory.${index}.supervisorContact`, FIELD_LIMITS.mediumText)} />
              </div>
              <div>
                <label>Can Contact Supervisor</label>
                <select className="themed-input" {...register(`workHistory.${index}.canContactSupervisor`)}>
                  <option value="">Select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <button type="button" onClick={() => workArray.remove(index)}>
                Remove Work Experience
              </button>
              </div>
              )}
            </div>
          );
        })}
        <button
          type="button"
          disabled={workArray.fields.length >= 40}
          onClick={() =>
            workArray.append({
              jobTitle: '',
              employer: '',
              employerAddress: '',
              startDate: '',
              endDate: '',
              present: false,
              showOnResume: true,
              hoursPerWeek: '',
              responsibilities: '',
              enhancedResponsibilities: [],
              enhancedAccomplishments: [],
              keyAccomplishments: '',
              payAmount: '',
              payRate: '',
              employmentType: '',
              supervisor: '',
              supervisorContact: '',
              canContactSupervisor: '',
            })
          }
        >
          Add Work Experience
        </button>
        {/* Inspector for work history */}
        <SectionInspector
          sectionName="workHistory"
          formData={formData}
          setFormData={setFormData}
          setFieldValue={setValue}
          tokenThreshold={tokenThreshold}
          setTokenUsage={setTokenUsage}
          setUsagePercentage={setUsagePercentage}
        />
      </div>
    );
  };

  // Render volunteer work entries. Similar to work history but with fewer
  // fields. A present checkbox disables the end date input.
  const renderVolunteerWork = () => {
    return (
      <div className="phase-section">
        <h2>Volunteer Work</h2>
        {volunteerArray.fields.map((field, index) => {
          const present = watch(`volunteerWork.${index}.present`);
          return (
            <div key={field.id} className="entry">
              <h3>Volunteer Experience {index + 1}</h3>
              <div>
                <label>Organization</label>
                <input className="themed-input"{...lineField(`volunteerWork.${index}.organization`, FIELD_LIMITS.mediumText)} />
              </div>
              <div>
                <label>Role</label>
                <input className="themed-input"{...lineField(`volunteerWork.${index}.role`, FIELD_LIMITS.mediumText)} />
              </div>
              <div>
                <label>Start Date</label>
                <input className="themed-input"
                  type="date"
                  {...register(`volunteerWork.${index}.startDate`)}
                />
              </div>
              <div>
                <label>End Date</label>
                <input className="themed-input"
                  type="date"
                  {...register(`volunteerWork.${index}.endDate`)}
                  disabled={present}
                />
                <label style={{ marginLeft: '10px' }}>
                  <input type="checkbox" {...register(`volunteerWork.${index}.present`)} /> Present
                </label>
              </div>
              <div>
                <label>Hours Per Week</label>
                <input className="themed-input"
                  type="text"
                  {...integerField(`volunteerWork.${index}.hoursPerWeek`)}
                />
              </div>
              <div>
                <label>Responsibilities</label>
                <textarea className="themed-input"
                  {...multilineField(`volunteerWork.${index}.responsibilities`, FIELD_LIMITS.longText)}
                  rows={3}
                ></textarea>
              </div>
              <button
                type="button"
                onClick={() => volunteerArray.remove(index)}
              >
                Remove Volunteer Experience
              </button>
            </div>
          );
        })}
        <button
          type="button"
          disabled={volunteerArray.fields.length >= 30}
          onClick={() =>
            volunteerArray.append({
              organization: '',
              role: '',
              startDate: '',
              endDate: '',
              present: false,
              hoursPerWeek: '',
              responsibilities: '',
            })
          }
        >
          Add Volunteer Experience
        </button>
        {/* Inspector for volunteer work */}
        <SectionInspector
          sectionName="volunteerWork"
          formData={formData}
          setFormData={setFormData}
          setFieldValue={setValue}
          tokenThreshold={tokenThreshold}
          setTokenUsage={setTokenUsage}
          setUsagePercentage={setUsagePercentage}
        />
      </div>
    );
  };

  // Render skills. Each skill is a simple string. Users can add or remove
  // skills. The list of selected skills will be reflected in formData.
  const renderSkills = () => {
    return (
      <div className="phase-section">
        <h2>Skills</h2>

        {/* Collapsible container for the O4 skills search and recommendation widget. */}
        <details className="collapsible-section" >
          <summary>Skill Search &amp; Suggestions</summary>
          <div className="collapsible-content">
            {/*
              Embed the O4 skills recommendation component directly into the
              skills phase. When the user selects or deselects recommended
              skills, the handleO4FormDataUpdate wrapper ensures both the
              top‑level formData and the local form state stay in sync.
            */}
            <O4SkillsScreen
              formData={formData}
              setFormData={handleO4FormDataUpdate}
              setTokenUsage={setTokenUsage}
              setUsagePercentage={setUsagePercentage}
              tokenThreshold={tokenThreshold}
            />
          </div>
        </details>

        {/* Collapsible container for the editable list of skills. */}
        <details className="collapsible-section"  style={{ marginTop: '1rem' }}>
          <summary>Skill List</summary>
          <div className="collapsible-content">
            {/* Render each skill as an editable field with remove support. The
                underlying react‑hook‑form array ensures these edits are
                reflected in the global formData via the watch subscription. */}
            <button type="button" onClick={() => skillsArray.append('')}
              disabled={skillsArray.fields.length >= 80}
              style={{ marginTop: '0.5rem' }}
            >
              Add Skill
            </button>
            {skillsArray.fields.map((field, index) => (
              <div key={field.id} className="entry" style={{ display: 'flex', alignItems: 'center' }}>
              <input
                className="themed-input"
                {...lineField(`skills.${index}`, FIELD_LIMITS.listItem)}
                style={{ flexGrow: 1 }}
              />
              <button
                type="button"
                onClick={() => skillsArray.remove(index)}
                aria-label="Remove item"
                style={{ marginLeft: '0.5rem' }}
              >
                🗑
              </button>
            </div>

            ))}

          </div>
        </details>

        {/* Inspector for skills.  Keep this outside of collapsible areas so that
            validation controls remain visible regardless of the toggled
            state of the search and list. */}
        <SectionInspector
          sectionName="skills"
          formData={formData}
          setFormData={setFormData}
          setFieldValue={setValue}
          tokenThreshold={tokenThreshold}
          setTokenUsage={setTokenUsage}
          setUsagePercentage={setUsagePercentage}
        />
      </div>
    );
  };

  // Render certifications. Each certification has a name, authority,
  // and expiry date. Users can add or remove certifications.
  const renderCertifications = () => {
    return (
      <div className="phase-section">
        <h2>Certifications</h2>
        {certificationsArray.fields.map((field, index) => (
          <div key={field.id} className="entry">
            <div>
              <label>Name</label>
              <input className="themed-input"
                {...lineField(`certifications.${index}.certificationName`, FIELD_LIMITS.mediumText)}
              />
            </div>
            <div>
              <label>Authority</label>
              <input className="themed-input"
                {...lineField(
                  `certifications.${index}.certificationAuthority`,
                  FIELD_LIMITS.mediumText,
                )}
              />
            </div>
            <div>
              <label>Expiry Date</label>
              <input className="themed-input"
                type="date"
                {...register(`certifications.${index}.expiryDate`)}
              />
            </div>
            <button
              type="button"
              onClick={() => certificationsArray.remove(index)}
            >
              Remove Certification
            </button>
          </div>
        ))}
        <button
          type="button"
          disabled={certificationsArray.fields.length >= 50}
          onClick={() =>
            certificationsArray.append({
              certificationName: '',
              certificationAuthority: '',
              expiryDate: '',
            })
          }
        >
          Add Certification
        </button>
        {/* Inspector for certifications */}
        <SectionInspector
          sectionName="certifications"
          formData={formData}
          setFormData={setFormData}
          setFieldValue={setValue}
          tokenThreshold={tokenThreshold}
          setTokenUsage={setTokenUsage}
          setUsagePercentage={setUsagePercentage}
        />
      </div>
    );
  };

  // Render training entries. Each entry is a free‑form string. Users can
  // add or remove training courses.
  const renderTraining = () => {
    return (
      <div className="phase-section">
        <h2>Training</h2>
        {trainingArray.fields.map((field, index) => (
          <div key={field.id} className="entry">
            <input className="themed-input"{...lineField(`training.${index}`, FIELD_LIMITS.listItem)} />
            <button
              type="button"
              onClick={() => trainingArray.remove(index)}
            >
              Remove
            </button>
          </div>
        ))}
        <button type="button" disabled={trainingArray.fields.length >= 80} onClick={() => trainingArray.append('')}>
          Add Training
        </button>
        {/* Inspector for training */}
        <SectionInspector
          sectionName="training"
          formData={formData}
          setFormData={setFormData}
          setFieldValue={setValue}
          tokenThreshold={tokenThreshold}
          setTokenUsage={setTokenUsage}
          setUsagePercentage={setUsagePercentage}
        />
      </div>
    );
  };

  // Render references. Each reference includes name, email, and phone. A
  // relationship field could be added later if desired.
  const renderReferences = () => {
    return (
      <div className="phase-section">
        <h2>References</h2>
        {referencesArray.fields.map((field, index) => (
          <div key={field.id} className="entry">
            <div>
              <label>Name</label>
              <input className="themed-input"{...lineField(`references.${index}.name`, FIELD_LIMITS.mediumText)} />
            </div>
            <div>
              <label>Email</label>
              <input className="themed-input" type="email" {...lineField(`references.${index}.email`, FIELD_LIMITS.email, { pattern: PATTERNS.email })} />
            </div>
            <div>
              <label>Phone</label>
              <input className="themed-input" type="tel" {...lineField(`references.${index}.phone`, FIELD_LIMITS.phone, { pattern: PATTERNS.phone })} />
            </div>
            <button
              type="button"
              onClick={() => referencesArray.remove(index)}
            >
              Remove Reference
            </button>
          </div>
        ))}
        <button
          type="button"
          disabled={referencesArray.fields.length >= 20}
          onClick={() =>
            referencesArray.append({ name: '', email: '', phone: '' })
          }
        >
          Add Reference
        </button>

      </div>
    );
  };

  // Render languages. Each entry is a free‑form string.
  const renderLanguages = () => {
    return (
      <div className="phase-section">
        <h2>Languages</h2>
        {languagesArray.fields.map((field, index) => (
          <div key={field.id} className="entry">
            <input className="themed-input"{...lineField(`languages.${index}`, FIELD_LIMITS.listItem)} />
            <button
              type="button"
              onClick={() => languagesArray.remove(index)}
            >
              Remove
            </button>
          </div>
        ))}
        <button type="button" disabled={languagesArray.fields.length >= 40} onClick={() => languagesArray.append('')}>
          Add Language
        </button>
        {/* Inspector for languages */}
        <SectionInspector
          sectionName="languages"
          formData={formData}
          setFormData={setFormData}
          setFieldValue={setValue}
          tokenThreshold={tokenThreshold}
          setTokenUsage={setTokenUsage}
          setUsagePercentage={setUsagePercentage}
        />
      </div>
    );
  };

  // Render interests and hobbies. Each entry is a free‑form string.
  const renderInterests = () => {
    return (
      <div className="phase-section">
        <h2>Interests & Hobbies</h2>
        {interestsArray.fields.map((field, index) => (
          <div key={field.id} className="entry">
            <input className="themed-input"{...lineField(`interests.${index}`, FIELD_LIMITS.listItem)} />
            <button
              type="button"
              onClick={() => interestsArray.remove(index)}
            >
              Remove
            </button>
          </div>
        ))}
        <button type="button" disabled={interestsArray.fields.length >= 60} onClick={() => interestsArray.append('')}>
          Add Interest
        </button>
        {/* Inspector for interests */}
        <SectionInspector
          sectionName="interests"
          formData={formData}
          setFormData={setFormData}
          setFieldValue={setValue}
          tokenThreshold={tokenThreshold}
          setTokenUsage={setTokenUsage}
          setUsagePercentage={setUsagePercentage}
        />
      </div>
    );
  };

  // Render awards and honors. Each entry is a free‑form string.
  const renderAwards = () => {
    return (
      <div className="phase-section">
        <h2>Awards & Honors</h2>
        {awardsArray.fields.map((field, index) => (
          <div key={field.id} className="entry">
            <input className="themed-input"{...lineField(`awards.${index}`, FIELD_LIMITS.listItem)} />
            <button
              type="button"
              onClick={() => awardsArray.remove(index)}
            >
              Remove
            </button>
          </div>
        ))}
        <button type="button" disabled={awardsArray.fields.length >= 60} onClick={() => awardsArray.append('')}>
          Add Award
        </button>
        {/* Inspector for awards */}
        <SectionInspector
          sectionName="awards"
          formData={formData}
          setFormData={setFormData}
          setFieldValue={setValue}
          tokenThreshold={tokenThreshold}
          setTokenUsage={setTokenUsage}
          setUsagePercentage={setUsagePercentage}
        />
      </div>
    );
  };

  // Render organizations. Each entry is a free‑form string.
  const renderOrganizations = () => {
    return (
      <div className="phase-section">
        <h2>Organizations</h2>
        {organizationsArray.fields.map((field, index) => (
          <div key={field.id} className="entry">
            <input className="themed-input"{...lineField(`organizations.${index}`, FIELD_LIMITS.listItem)} />
            <button
              type="button"
              onClick={() => organizationsArray.remove(index)}
            >
              Remove
            </button>
          </div>
        ))}
        <button type="button" disabled={organizationsArray.fields.length >= 60} onClick={() => organizationsArray.append('')}>
          Add Organization
        </button>
        {/* Inspector for organizations */}
        <SectionInspector
          sectionName="organizations"
          formData={formData}
          setFormData={setFormData}
          setFieldValue={setValue}
          tokenThreshold={tokenThreshold}
          setTokenUsage={setTokenUsage}
          setUsagePercentage={setUsagePercentage}
        />
      </div>
    );
  };

  // Determine which section to render based on the current phase.
  const renderPhase = () => {
    switch (currentPhase) {
      case 'personalInfo':
        return renderPersonalInfo();
      case 'professionalSummary':
        return renderProfessionalSummary();
      case 'militaryInfo':
        return renderMilitaryInfo();
      case 'educationHistory':
        return renderEducationHistory();
      case 'workHistory':
        return renderWorkHistory();
      case 'volunteerWork':
        return renderVolunteerWork();
      case 'skills':
        return renderSkills();
      case 'certifications':
        return renderCertifications();
      case 'training':
        return renderTraining();
      case 'references':
        return renderReferences();
      case 'languages':
        return renderLanguages();
      case 'interests':
        return renderInterests();
      case 'awards':
        return renderAwards();
      case 'organizations':
        return renderOrganizations();
      default:
        return null;
    }
  };

  // Render the phase navigation, an animated progress bar and the active
  // phase content. Buttons disable themselves when selected. The
  // progress bar illustrates how far the user has advanced through the
  // résumé‑building process.
  return (
    <div className="form-container">
      <div className="phase-nav">
        {phases.map((phase) => (
          <button
            key={phase}
            type="button"
            onClick={() => setCurrentPhase(phase)}
            disabled={currentPhase === phase}
            aria-current={currentPhase === phase ? 'step' : undefined}
          >
            {capitalizePhase(phase)}
          </button>
        ))}
      </div>
      {/* Progress indicator showing completion across phases */}
      <div
        className="progress-container"
        role="progressbar"
        aria-label="Resume form progress"
        aria-valuemin="1"
        aria-valuemax={phases.length}
        aria-valuenow={phases.indexOf(currentPhase) + 1}
        aria-valuetext={`${capitalizePhase(currentPhase)}, step ${phases.indexOf(currentPhase) + 1} of ${phases.length}`}
      >
        <div
          className="progress-bar"
          style={{
            width: `${((phases.indexOf(currentPhase) + 1) / phases.length) * 100}%`,
          }}
        ></div>
      </div>
      <div className="phase-content">{renderPhase()}</div>
    </div>
  );
};

export default Form;

